/** Where the lap went, corner by corner, against the chosen reference.
 *
 * The lap analysis card above the fold is the logger's verdict on a lap
 * against the session's own best. This card is the same report against
 * whichever reference the "compare with" control names — another driver,
 * the circuit's best — worked out here from the two recordings, through the
 * reference lap's corner windows, so the per-corner figures and the delta
 * trace are one comparison added up two ways.
 *
 * Sorted by what each corner cost, biggest loss first, because the question
 * the card answers is "where am I losing it" and the answer should be the
 * first row. Beside the time, the three things a driver can change: where
 * the brake went on and how far from where the reference braked, how slow
 * the corner got, how fast it was left.
 *
 * The corners are the circuit's authored ones when it has them, which is what
 * makes T5 the same T5 on another driver's lap; otherwise they are detected on
 * the reference lap, and the numbering holds for this comparison only.
 */

import type { ReactNode } from 'react';

import { Card, Kicker, Spinner, TBody, TD, TH, THead, TR, Table } from '@/components/ui';
import { DASH, count, delta, deltaTone } from '@/lib/format';
import { speedIn, speedLabel } from '@/lib/units';
import { useSpeedUnit } from '@/lib/useSpeedUnit';
import {
  brakeBeforeApex,
  cornerLabel,
  cornerScale,
  totalLostMs,
  worstCornerRows,
} from '@/components/sessions/compare';
import type { CornerCompareRow } from '@/components/sessions/compare';
import type { Reference } from '@/components/sessions/references';

export interface CornersCardProps {
  lapNumber: number | null;
  reference: Reference | null;
  rows: readonly CornerCompareRow[];
  isReference: boolean;
  aligned: boolean;
  /** Whether the corners are the circuit's own rather than detected. */
  authored: boolean;
  /** The lap's gap at the line, to set beside what the corners add up to. */
  atLineMs: number | null;
  loading: boolean;
}

const OK = 'var(--color-ok)';
const WARN = 'var(--color-warn)';
const QUIET = 'var(--color-neutral-600)';
const MUTED = 'var(--color-neutral-500)';

const TONE: Record<ReturnType<typeof deltaTone>, string> = {
  warn: WARN,
  ok: OK,
  level: MUTED,
  none: MUTED,
};

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** `+4` / `−4`, for speeds and metres. */
function signed(value: number | null | undefined, digits = 0): string {
  if (!isNumber(value)) return DASH;
  const fixed = Math.abs(value).toFixed(digits);
  if (Number(fixed) === 0) return fixed;
  return `${value > 0 ? '+' : '−'}${fixed}`;
}

/** Speed gained is ok, speed given away is warn. */
function speedTone(kmh: number | null): string {
  if (!isNumber(kmh) || Math.abs(kmh) < 0.5) return MUTED;
  return kmh > 0 ? OK : WARN;
}

/** Braking later than the reference is the deeper, riskier choice, so it is
 *  coloured as a warning only when it cost time; the table says which. */
function brakeTone(metres: number | null, lostMs: number | null): string {
  if (!isNumber(metres) || Math.abs(metres) < 5) return MUTED;
  if (lostMs === null) return 'var(--color-neutral-300)';
  return lostMs > 5 ? WARN : OK;
}

export function CornersCard({
  lapNumber,
  reference,
  rows,
  isReference,
  aligned,
  authored,
  atLineMs,
  loading,
}: CornersCardProps) {
  const speedUnit = useSpeedUnit();
  const unit = speedLabel(speedUnit.unit);
  const speed = (kmh: number | null) => (isNumber(kmh) ? count(speedIn(kmh, speedUnit.unit)) : DASH);
  const speedDelta = (kmh: number | null) =>
    isNumber(kmh) ? signed(speedIn(kmh, speedUnit.unit)) : DASH;
  const comparing = !isReference && rows.some((row) => row.lostMs !== null);
  const scale = cornerScale(rows);
  const total = totalLostMs(rows);

  return (
    <Card elevation="none" label="Corners" style={{ padding: 14, gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <Kicker>
          corners · lap {lapNumber ?? DASH}
          {reference && !isReference ? ` vs ${reference.label}` : ''}
        </Kicker>
        <span style={{ fontSize: 11, color: MUTED }}>
          {authored ? 'the circuit’s corners' : 'corners found on the reference lap'}
        </span>
      </div>

      {rows.length === 0 ? (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: 80, fontSize: 12.5, color: QUIET }}>
          {loading ? <Spinner label="measuring the corners" /> : 'No corners could be placed on this lap.'}
        </div>
      ) : (
        <>
          {comparing ? (
            <Summary rows={rows} total={total} atLineMs={atLineMs} aligned={aligned} />
          ) : (
            <Note>
              {isReference
                ? 'This is the reference lap: its own figures, with nothing to compare against. Choose another lap or another driver.'
                : 'The reference did not drive these corners, so there is nothing to compare; the figures are this lap’s own.'}
            </Note>
          )}

          <Table
            scroll
            label="Corners"
            style={{ fontSize: 12.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
          >
            <THead>
              <TR>
                <TH>Corner</TH>
                {comparing ? <TH numeric>Δ ref</TH> : null}
                <TH numeric>Time</TH>
                <TH numeric>Brake on</TH>
                <TH numeric>{`Min ${unit}`}</TH>
                <TH numeric>{`Exit ${unit}`}</TH>
                <TH numeric>Turn</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => {
                const before = brakeBeforeApex(row);
                const brakeDelta = comparing ? row.row.brake_delta_m : null;
                return (
                  <TR key={row.n}>
                    <TD color={MUTED} title={row.name ? `T${row.n}` : undefined}>
                      {cornerLabel(row)}
                    </TD>
                    {comparing ? (
                      <TD numeric color={TONE[deltaTone(row.lostMs)]}>
                        <LossBar ms={row.lostMs} scale={scale} />
                        {row.lostMs === null ? DASH : delta(row.lostMs, 2)}
                      </TD>
                    ) : null}
                    <TD numeric>{(row.row.time_ms / 1000).toFixed(2)}</TD>
                    <TD numeric>
                      {row.row.brake_on === null ? (
                        <span style={{ color: QUIET }}>no brake</span>
                      ) : before === null ? (
                        `${count(Math.round(row.row.brake_on))} m`
                      ) : (
                        `${count(Math.round(Math.abs(before)))} m ${before >= 0 ? 'before' : 'after'}`
                      )}
                      {isNumber(brakeDelta) ? (
                        <span
                          style={{ color: brakeTone(brakeDelta, row.lostMs), marginLeft: 6 }}
                          title={
                            brakeDelta > 0
                              ? `braked ${Math.round(brakeDelta)} m later than the reference`
                              : `braked ${Math.round(-brakeDelta)} m earlier than the reference`
                          }
                        >
                          {signed(brakeDelta)} m
                        </span>
                      ) : null}
                    </TD>
                    <TD numeric>
                      {speed(row.row.min_speed)}
                      {comparing ? (
                        <span style={{ color: speedTone(row.minSpeedDelta), marginLeft: 6 }}>
                          {speedDelta(row.minSpeedDelta)}
                        </span>
                      ) : null}
                    </TD>
                    <TD numeric>
                      {speed(row.row.exit_speed)}
                      {comparing ? (
                        <span style={{ color: speedTone(row.exitSpeedDelta), marginLeft: 6 }}>
                          {speedDelta(row.exitSpeedDelta)}
                        </span>
                      ) : null}
                    </TD>
                    <TD numeric color={MUTED}>
                      {row.angle !== null ? `${Math.round(row.angle)}°` : DASH}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>

          <p style={{ margin: 0, fontSize: 11.5, color: QUIET, textWrap: 'pretty' }}>
            Brake points are metres before the apex; the small figure beside is where this lap braked
            against the reference, later being deeper. Differences are this lap minus the reference.
            {authored
              ? ''
              : ' Corners were found on the reference lap, so their numbers hold for this comparison only.'}
          </p>
        </>
      )}
    </Card>
  );
}

function Note({ children, tone = QUIET }: { children: ReactNode; tone?: string }) {
  return <p style={{ margin: 0, fontSize: 12.5, color: tone, textWrap: 'pretty' }}>{children}</p>;
}

/** One sentence: what the corners add up to and which ones mostly did it. */
function Summary({
  rows,
  total,
  atLineMs,
  aligned,
}: {
  rows: readonly CornerCompareRow[];
  total: number | null;
  atLineMs: number | null;
  aligned: boolean;
}) {
  const worst = worstCornerRows(rows);
  return (
    <p style={{ margin: 0, fontSize: 13, color: 'var(--color-neutral-300)', textWrap: 'pretty' }}>
      <span className="tabular-nums" style={{ color: TONE[deltaTone(total)] }}>
        {delta(total)} s
      </span>{' '}
      through the corners
      {atLineMs !== null ? (
        <>
          , <span className="tabular-nums">{delta(atLineMs)} s</span> over the lap
        </>
      ) : null}
      .{' '}
      {worst.length === 0 ? (
        'No corner gave away more than a couple of hundredths.'
      ) : (
        <>
          Most of it went at{' '}
          {worst.map((row, index) => (
            <span key={row.n}>
              {index > 0 ? (index === worst.length - 1 ? ' and ' : ', ') : ''}
              <strong style={{ fontWeight: 500 }}>{cornerLabel(row)}</strong>{' '}
              <span className="tabular-nums" style={{ color: WARN }}>
                {delta(row.lostMs, 2)}
              </span>
            </span>
          ))}
          .
        </>
      )}
      {!aligned ? ' The laps could not be lined up by position, so each corner is judged on its own distance.' : ''}
    </p>
  );
}

/** A bar either side of a centre line: time lost to the right, gained to the left. */
function LossBar({ ms, scale }: { ms: number | null; scale: number }) {
  const share = ms === null || scale === 0 ? 0 : Math.min(1, Math.abs(ms) / scale);
  return (
    <span
      aria-hidden
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 64,
        height: 8,
        verticalAlign: 'middle',
        marginRight: 8,
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: '50%',
          top: -2,
          bottom: -2,
          width: 1,
          background: 'var(--color-divider)',
        }}
      />
      {share > 0 ? (
        <span
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: `${share * 50}%`,
            ...(ms !== null && ms > 0 ? { left: '50%' } : { right: '50%' }),
            background: TONE[deltaTone(ms)],
            borderRadius: 2,
          }}
        />
      ) : null}
    </span>
  );
}

export default CornersCard;
